import { Collapse, Grid } from '@material-ui/core';
import { createStyles, makeStyles } from '@material-ui/core/styles';
import CollapseSection from 'components/collapse-section';
import { isArray } from 'lodash';
import React, { useState } from 'react';
import { Link } from 'react-router-dom';

const useSectionStyles = makeStyles(() =>
  createStyles({
    root: {
      paddingLeft: 8,
      borderBottom: '1px solid rgba(224, 224, 224, 1)',
    },
    title: {
      display: 'flex',
      justifyContent: 'flex-end',
      alignItems: 'center',
      paddingRight: '16px',
      height: '100%',
      lineHeight: '24px',
    },
    value: {
      lineHeight: '15px',
    },
  })
);

export type RegisterSectionProps = {
  title: string;
};

export const RegisterSection: React.FC<RegisterSectionProps> = ({
  title,
  children,
}) => {
  const classes = useSectionStyles();
  return (
    <Grid item xs={12} className={classes.root}>
      <CollapseSection
        title={
          <span style={{ fontSize: 18, lineHeight: '60px' }}>{title}</span>
        }
        defaultOpen
        overrideOpen={true}
      >
        {children}
      </CollapseSection>
    </Grid>
  );
};

const useSectionFieldStyles = makeStyles(() =>
  createStyles({
    wrapper: {
      cursor: 'pointer',
      '&:hover': {
        textDecoration: 'underline',
      },
    },
    root: {
      margin: '0 8px',
    },
    collapsedBody: {
      padding: '16px 16px 16px 24px',
      backgroundColor: 'rgb(150 150 150 / 30%)',
      margin: 16,
      border: '1px solid #282b34',
      //width: '100%',
    },
    label: {
      paddingRight: '8px',
      fontWeight: 'bold',
      lineHeight: '20px',
      minWidth: 200,
    },
    value: {
      fontWeight: 'normal',
      minWidth: 200,
    },
    link: {
      color: '#000',
      fontWeight: 'normal',
      '&:hover': {
        textDecoration: 'underline',
      },
    },
  })
);

export const RegisterSectionField: React.FC<{
  label?: string;
  value: string | React.ReactNode;
  plainText?: boolean;
  directLink?: string;
  externLink?: boolean;
}> = ({ label, value, children, plainText, directLink, externLink }) => {
  const classes = useSectionFieldStyles();
  const [collapsed, setCollapsed] = useState(false);
  if (!value) {
    return <></>;
  }
  if (plainText) {
    return (
      <div>
        {label && <span className={classes.label}>{label}:</span>}
        <span className={classes.value}>{value}</span>
      </div>
    );
  }
  if (directLink) {
    return (
      <div>
        {label && <span className={classes.label}>{label}:</span>}
        {externLink ? (
          <a
            href={directLink}
            target="_blank"
            rel="noreferrer"
            className={classes.link}
          >
            {value}
          </a>
        ) : (
          <Link to={directLink} className={classes.link}>
            {value}
          </Link>
        )}
      </div>
    );
  }

  return (
    <div>
      <div onClick={() => setCollapsed((p) => !p)} className={classes.wrapper}>
        {label && <span className={classes.label}>{label}:</span>}
        <span className={classes.value}>{value}</span>
      </div>
      <Collapse in={collapsed} className={classes.root}>
        <div className={classes.collapsedBody}>{children}</div>
      </Collapse>
    </div>
  );
};

type RegisterItemDetailProps<T extends Record<string, string | string[]>> = {
  item: T;
  keyLabels: Record<keyof T, string>;
};

export const RegisterItemDetail = <
  T extends Record<string, string | string[]>
>({
  item,
  keyLabels,
}: RegisterItemDetailProps<T>) => {
  const classes = useSectionFieldStyles();
  if (!item) {
    return <></>;
  }
  return (
    <>
      {Object.keys(keyLabels)
        .filter((key) => {
          if (isArray(item?.[key])) {
            return item[key]?.length > 0;
          }
          return item?.[key];
        })
        .map((key) => (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <span className={classes.label} style={{ textAlign: 'right' }}>
              {keyLabels?.[key]}:
            </span>
            <span
              className={classes.value}
              style={{ width: 'auto', textAlign: 'left' }}
            >
              {isArray(item?.[key]) ? (
                <>{(item?.[key] as string[]).join(', ')}</>
              ) : (
                <>{item?.[key]}</>
              )}
            </span>
          </div>
        ))}
    </>
  );
};
